<template>
	<div class="receipt-manager">
		<AppBottomSheet :z-index="1025" :force-width="transactionsBottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="receipt-manager-content" v-if="active">
                <ModalHeader
                    :title="getString('pos_receipt')" />
                <div class="pos-receipt-container">
                    <AppList :items="items">
                        <template v-slot:chevron-slot="{item}">
                            <div class="row centered space-between row-clickable chevron-slot" @click="item.active = !item.active">
                                <p class="list-item-title">{{ item.title }}</p>
                                <div class="chevron-container">
                                    <AppChevronIcon :direction="item.active ? 'up' : 'down'" />
                                </div>
                            </div>
                        </template>
                        <template v-slot:email-dropdown="{item}">
                            <div class="email-container">
                                <div class="ml-auto"><AppTextInput :placeholder="getString('email')" ref="email-receipt" type="email" /></div>
                                <div class="ml-auto mt-10"><AppButton :text="getString('send')" @click="sendEmailReceipt" :loading="loadingSendEmail" /></div>
                            </div>
                        </template>
                    </AppList>
                </div>
            </div>
		</AppBottomSheet>
		<AppNotification :title="getString('error')" :body-text="getString('invalid_email')" ref="email-error" :type="-2" />
		<AppNotification :title="getString('error')" :body-text="getString('generic_error')" ref="generic-error" :type="-2" />
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useOrdersActionsStore, useOrdersStore, useUserStore} from "@/console-new/stores";
import AppList from "@/shared/components/AppList.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppButton from "@/shared/components/AppButton.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNotification from "@/shared/components/AppNotification.vue";

import {validateEmail} from "@/shared/helpers/strings";

import API from "@/shared/helpers/API";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";
import {buildPosReceiptEscPos} from "@/console-pos/helpers/PosReceipt";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const ordersActionsStore = useOrdersActionsStore();
		const ordersStore = useOrdersStore();
		const sunmiStore = useSunmiStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, userStore, ordersActionsStore, ordersStore, sunmiStore, currencyStore};
	},
	name: "ReceiptManager",
	components: {AppNotification, AppTextInput, AppChevronIcon, AppButton, ModalHeader, AppBottomSheet, AppList},
	mixins: [WindowWidth],
	emits: ['success', 'toggling-popup'],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		item: {
			type: Object,
			default: null,
		},
		paymentId: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			loadingSendEmail: false,
			loadingPrint: false,
		};
	},
	computed: {
		transactionsBottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		items() {
			let items = [
				{title: this.getString('print_receipt'), buttonTitle: this.getString('print'), buttonLoading: this.loadingPrint, callbackButton: this.printReceipt},
			];

			if(!!this.paymentId || !!this.item || (this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1)) {
				items.push({title: this.getString('email_receipt'), customSlot: 'chevron-slot', active: false, dropdownSlot: 'email-dropdown'});
			}

			return items;
		},
	},
	methods: {
		buildReceiptEscPos(transaction) {
			return buildPosReceiptEscPos(this.userStore.currBusiness, transaction, this.currencyStore.currCurrency?.code);
		},
		resolveTransactionId() {
			if(this.item) {
				return this.item.transaction_id;
			} else if(this.paymentId) {
				return "p"+this.paymentId;
			} else if(this.ordersStore.selectedOrderGroup) {
				return "o"+JSON.stringify(this.ordersStore.selectedOrderGroup.orders.map(o => o.order_id));
			}
			return null;
		},
		printReceipt() {

			if(this.sunmiStore.canLocalPrint) {

				if(this.item) {
					this.sunmiStore.enqueuePrint(this.buildReceiptEscPos(this.item));
					this.sendCloudPrint();
				} else {
					let id = this.resolveTransactionId();
					if(!id) {
						this.$refs['generic-error'].openNotification();
						return;
					}

					this.loadingPrint = true;
					API.init().getPosReceiptData(this.userStore.currBusinessId, id, (r) => {
						if(r && r.result === "OK" && r?.transactions?.length) {
							for(let transaction of r.transactions) {
								this.sunmiStore.enqueuePrint(this.buildReceiptEscPos(transaction));
							}
						}
						this.sendCloudPrint();
					});
				}

			} else {
				this.sendCloudPrint();
			}

		},
		sendCloudPrint() {
			let id = this.resolveTransactionId();
			if(!id) {
				this.loadingPrint = false;
				return;
			}

			this.loadingPrint = true;
			this.ordersActionsStore.printPosReceipts(id, () => {
				this.loadingPrint = false;
			});
		},
		sendEmailReceipt() {

			let email = this.$refs['email-receipt'].currentValue;
			if(!validateEmail(email)) {
				this.$refs['email-error'].openNotification();
			} else {

				//very smelly code :(
				let id = null;
				if(this.item) {
					id = this.item.transaction_id;
				} else if(this.paymentId) {
					id = "p"+this.paymentId;
				} else if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1) {
					id = "o"+this.ordersStore.selectedOrderGroup.orders[0].order_id;
				}
				if(!id) {
					this.$refs['generic-error'].openNotification();
					return;
				}
				this.loadingSendEmail = true;
				API.init().sendTransactionReceiptEmail(this.userStore.currBusinessId, email, id, (r) => {
					this.loadingSendEmail = false;
					if(r && r.result && r.result === "OK") {
						this.$refs['email-receipt'].currentValue = "";
						this.$emit("toggling-popup", false);
						this.$emit("success");
					} else {
						this.$refs['generic-error'].openNotification();
					}
				});

			}

		},
	},
}
</script>

<style scoped lang="scss">
.ml-auto {
	margin-left: auto;
}

.chevron-slot .list-item-title {
	width: calc(100% - 20px);
}
.email-container {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: center;
	width: 100%;

}

.row-clickable {
	cursor: pointer;

	.chevron-container {
		height: 35px;
		display: flex;
		align-items: center;
	}
}
</style>